import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Home, 
  Award, 
  BookOpen, 
  CheckCircle, 
  XCircle, 
  ArrowRight,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import scenarioService, {
  ScenarioQuestion,
  ScenarioOutcome,
  ScenarioGameResult,
  ScenarioChoice,
  ScenarioStatus
} from '../services/scenarioService';
import '../components/HomeReturnButton.css';
import './scenario-game.css';

const ScenarioGamePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth0();

  // Game state
  const [scenarios, setScenarios] = useState<ScenarioQuestion[]>([]);
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [gameChoices, setGameChoices] = useState<ScenarioChoice[]>([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scenarioStatus, setScenarioStatus] = useState<ScenarioStatus | null>(null);

  // Get current scenario
  const currentScenario = scenarios[currentScenarioIndex];

  // Load scenarios on mount
  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check scenario status first (unlimited games now)
      if (user?.sub) {
        const status = await scenarioService.getScenarioStatus(user.sub);
        setScenarioStatus(status);

        // Only block if gamesRemaining is exactly 0 (not -1 for unlimited)
        if (status.gamesRemaining === 0) {
          setError('Unable to load scenarios. Please try again later.');
          setIsLoading(false);
          return;
        }
      }

      // Load random scenarios
      const result = await scenarioService.getRandomScenarios(3);
      setScenarios(result.scenarios);
      setIsLoading(false);
    } catch (err) {
      console.error('Error loading scenarios:', err);
      setError('Failed to load scenarios. Please try again.');
      setIsLoading(false);
    }
  };

  const handleAnswerSelect = (choiceText: string) => {
    if (showResult) return;

    setSelectedAnswer(choiceText);
    setShowResult(true);

    // Find the selected outcome
    const selectedOutcome = currentScenario?.scenarioDetails?.outcomes.find(
      outcome => outcome.choiceText === choiceText
    );

    if (selectedOutcome) {
      // Add to game choices
      const choice: ScenarioChoice = {
        scenarioId: currentScenario._id,
        choiceText: selectedOutcome.choiceText,
        points: selectedOutcome.points,
        isRecommended: selectedOutcome.isRecommended
      };

      setGameChoices(prev => [...prev, choice]);
      setTotalPoints(prev => prev + selectedOutcome.points);
    }
  };

  const handleNextScenario = () => {
    if (currentScenarioIndex < scenarios.length - 1) {
      setCurrentScenarioIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      
      // Scroll to the top of the next question title after a short delay
      setTimeout(() => {
        const titleElement = document.querySelector('.scenario-title-container');
        if (titleElement) {
          titleElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Game complete
      submitResults();
    }
  };

  const submitResults = async () => {
    // If user is not authenticated, just navigate back to home
    if (!user?.sub) {
      navigate('/');
      return;
    }

    try {
      const result: ScenarioGameResult = {
        pointsEarned: totalPoints,
        correctAnswers: gameChoices.filter(choice => choice.isRecommended).length,
        totalQuestions: scenarios.length,
        scenarios: gameChoices
      };

      await scenarioService.submitScenarioResults(user.sub, result);

      // Navigate to results page with scenario data
      navigate('/results', {
        state: {
          gameType: 'scenario',
          score: totalPoints,
          totalQuestions: scenarios.length,
          correctAnswers: gameChoices.filter(choice => choice.isRecommended).length,
          answers: gameChoices.map(choice => ({
            questionId: choice.scenarioId,
            userAnswer: choice.choiceText,
            isCorrect: choice.isRecommended,
            pointsEarned: choice.points,
            resultText: currentScenario?.scenarioDetails?.outcomes.find(
              outcome => outcome.choiceText === choice.choiceText
            )?.resultText || ''
          })),
          scenarios: scenarios
        }
      });
    } catch (err) {
      console.error('Error submitting results:', err);
      setError('Failed to submit results. Please try again.');
    }
  };

  const getSelectedOutcome = (): ScenarioOutcome | undefined => {
    if (!selectedAnswer || !currentScenario?.scenarioDetails) return undefined;
    return currentScenario.scenarioDetails.outcomes.find(
      outcome => outcome.choiceText === selectedAnswer
    );
  };

  if (isLoading) {
    return (
      <div className="scenario-loading-container">
        <div className="scenario-loading-text">Loading scenarios...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scenario-error-container">
        <div className="scenario-error-content">
          <div className="scenario-error-message">{error}</div>
          <button
            onClick={() => navigate('/')}
            className="scenario-error-button"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!currentScenario) {
    return (
      <div className="scenario-loading-container">
        <div className="scenario-loading-text">No scenarios available</div>
      </div>
    );
  }

  const selectedOutcome = getSelectedOutcome();

  return (
    <div className="scenario-game-container">
      {/* Header */}
      <div className="scenario-header">
        <button
          onClick={() => navigate('/')}
          className="scenario-home-button"
        >
          <Home size={20} />
          <span className="scenario-home-text">Home</span>
        </button>
        <div className="scenario-title-section">
          <h1 className="scenario-main-title">
            Biblical Scenarios
          </h1>
          <div className="scenario-progress-text">
            Question {currentScenarioIndex + 1} of {scenarios.length}
          </div>
        </div>
        <div className="scenario-points-section">
          <div className="scenario-points-content">
            <div className="scenario-points-label">Points</div>
            <div className="scenario-points-value">{totalPoints}</div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="scenario-progress-container">
        <div className="scenario-progress-bar">
          <div
            className="scenario-progress-fill"
            style={{ width: `${((currentScenarioIndex + 1) / scenarios.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Scenario Content */}
      <div className="scenario-content">
        {/* Scenario Title */}
        <div className="scenario-title-container">
          <h2 className="scenario-title">{currentScenario.title}</h2>
          {currentScenario.bibleReference && (
            <div className="scenario-bible-reference">
              {currentScenario.bibleReference.book} {currentScenario.bibleReference.chapter}:{currentScenario.bibleReference.verses}
            </div>
          )}
        </div>

        {/* Scenario Story */}
        <div className="scenario-story-container">
          <div className="scenario-story-header">
            <Sparkles size={20} className="scenario-story-icon" />
            <h3 className="scenario-story-title">The Situation</h3>
          </div>
          <p className="scenario-story-text">{currentScenario.story}</p>
        </div>

        {/* Question Prompt */}
        <div className="scenario-prompt-container">
          <AlertCircle size={24} className="scenario-prompt-icon" />
          <h3 className="scenario-prompt-title">What would you do?</h3>
        </div>

        {/* Options */}
        <div className="scenario-options-container">
          {currentScenario.scenarioDetails?.outcomes.map((outcome, index) => (
            <button
              key={outcome._id}
              onClick={() => handleAnswerSelect(outcome.choiceText)}
              disabled={showResult}
              className={`scenario-option-button ${
                selectedAnswer === outcome.choiceText
                  ? showResult
                    ? outcome.isRecommended
                      ? 'scenario-option-correct'
                      : 'scenario-option-incorrect'
                    : 'scenario-option-selected'
                  : 'scenario-option-default'
              } ${showResult ? 'scenario-option-disabled' : 'scenario-option-active'}`}
            >
              <div className="scenario-option-content">
                <span className="scenario-option-text">{outcome.choiceText}</span>
                {showResult && selectedAnswer === outcome.choiceText && (
                  <div className="scenario-option-result">
                    <span className="scenario-option-points">
                      <Award size={16} /> {outcome.points} pts
                    </span>
                    {outcome.isRecommended ? (
                      <span className="scenario-option-recommended">
                        <CheckCircle size={16} /> Recommended
                      </span>
                    ) : (
                      <span className="scenario-option-not-recommended">
                        <XCircle size={16} /> Not recommended
                      </span>
                    )}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Result Display */}
        {showResult && selectedOutcome && (
          <div className={`scenario-result-container ${
            selectedOutcome.isRecommended ? 'scenario-result-positive' : 'scenario-result-negative'
          }`}>
            <div className="scenario-result-header">
              {selectedOutcome.isRecommended ? (
                <CheckCircle size={24} className="scenario-result-icon" />
              ) : (
                <XCircle size={24} className="scenario-result-icon" />
              )}
              <h4 className="scenario-result-title">
                {selectedOutcome.isRecommended ? 'Good Choice!' : 'Consider This...'}
              </h4>
            </div>
            <p className="scenario-result-text">{selectedOutcome.resultText}</p>
          </div>
        )}

        {/* Next Button */}
        {showResult && (
          <div className="scenario-action-container">
            <button
              onClick={handleNextScenario}
              className="scenario-action-button"
            >
              {currentScenarioIndex < scenarios.length - 1 ? (
                <>
                  Next Scenario
                  <ArrowRight size={20} className="scenario-action-icon" />
                </>
              ) : (
                <>
                  Complete Game
                  <CheckCircle size={20} className="scenario-action-icon" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScenarioGamePage;
